import { useState, useEffect } from 'react';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  AreaChart, Area
} from 'recharts';
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from '@/components/ui/card';
import { DashboardCard, StatusCard } from '@/components/ui/dashboard-card';
import { LoadingCard } from '@/components/ui/loading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { formatCurrency, formatDate, formatPercentage } from '@/lib/utils';
import { isAchPaymentAvailable } from '@/utils/paymentGateways';
import { getEnhancedDashboardMetrics, getCompanySettings } from '@/lib/api';
import {
  ArrowUpRight, ArrowDownRight, DollarSign, AlertCircle, CheckCircle2, Clock,
  TrendingUp, TrendingDown, Users, ShoppingBag, CreditCard, BarChart2, FileText,
  Calendar as CalendarIcon, Info, Bell, Receipt, Percent,
  Building2, Filter, ArrowUpDown, Eye,
  Activity, Timer, RefreshCcw
} from 'lucide-react';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { useAuth } from '@/components/AuthProvider';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { updateProduct } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

// Define interfaces for our components
interface StatusIndicatorProps {
  value: number;
  threshold?: number;
  positiveIsGood?: boolean;
  prefix?: string;
  suffix?: string;
}

interface EmptyStateProps {
  message?: string;
  icon?: React.ElementType;
}

// Removed conflicting DashboardCardProps interface - using the imported component

interface RefreshButtonProps {
  onClick: () => void;
  isLoading?: boolean;
}

interface ChartHeaderProps {
  title: string;
  description?: string;
  action?: React.ReactNode;
}

// Add these type definitions at the top of the file where other interfaces are defined
interface InvoiceStatus {
  status: string;
  value: number;
}

interface RecentActivity {
  status: string;
  date: string;
  amount: number;
  paymentMethod: string;
  customerName?: string;
  customerCompanyName?: string;
  cardLast4?: string;
}

interface CategoryData {
  name: string;
  category: string;
  margin: number;
  profit: number;
}

interface CustomerAgingData {
  name: string;
  current: number;
  days30: number;
  days60: number;
  days90: number;
  days90plus: number;
  total: number;
}

interface CashFlowForecast {
  date: string;
  // Add other properties as needed
}

interface CustomerType {
  name: string;
  value: number;
  revenue: number;
}

interface Customer {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  type?: string;
  invoicesCount?: number;
  totalSpent?: number;
  percentageOfRevenue?: number;
  avgDaysToPay?: number;
  onTimeRate?: number;
  preferredPaymentMethod?: string;
  status?: string;
}

interface Product {
  name: string;
  category?: string;
  categoryName?: string;
  unitsSold?: number;
  revenue?: number;
  cost?: number;
  profit?: number;
  margin?: number;
  stockQuantity?: number;
  lowStockAlert?: number;
  sku?: string;
  status?: string;
}

interface Transaction {
  id: string;
  date: string;
  customerName?: string;
  customerCompanyName?: string;
  amount: number;
  paymentMethod: string;
  cardLast4?: string;
  status: string;
  transactionId?: string;
}

interface Metrics {
  invoiceStatusDistribution?: InvoiceStatus[];
  recentActivity?: RecentActivity[];
  categoryProfitability?: CategoryData[];
  customerAging?: CustomerAgingData[];
  cashFlowForecast?: CashFlowForecast[];
  customerTypeDistribution?: CustomerType[];
  topCustomers?: Customer[];
  payingCustomers?: Customer[];
  productStatus?: { status: string; value: number }[];
  productsInventory?: Product[];
  recentTransactions?: Transaction[];
  totalRevenue?: number;
  totalInvoices?: number;
  alerts?: any[]; // Add proper type if known
  revenueGrowth?: number;
  totalCreditCardPayments?: number;
  totalACHPayments?: number;
  totalSalesTax?: number;
  totalPlatformFees?: number;
  outstandingAmount?: number;
  outstandingInvoices?: number;
  overdueAmount?: number;
  overdueInvoices?: number;
  aging30Days?: number;
  aging60Days?: number;
  aging90Days?: number;
  aging90PlusDays?: number;
  collectionRate?: number;
  projectedRevenue?: number;
  recurringRevenuePercentage?: number;
  paymentMethodDistribution?: {
    creditCard?: number;
    ach?: number;
  };
  topProductsByRevenue?: {
    id: string;
    name: string;
    revenue: number;
    unitsSold: number;
  }[];
  inventoryHealthDistribution?: { status: string; value: number }[];
  productProfitability?: Product[];
  // Add other metric properties as needed
  topSellingProduct?: {
    name: string;
    revenue: number;
    unitsSold: number;
  };
  mostProfitableProduct?: {
    name: string;
    margin: number;
    profit: number;
  };
}

// Reusable status indicator component to reduce duplication
const StatusIndicator = ({ value, threshold = 0, positiveIsGood = true, prefix = '', suffix = '' }: StatusIndicatorProps) => {
  const isPositive = value >= threshold;
  const isGood = (positiveIsGood && isPositive) || (!positiveIsGood && !isPositive);
  
  return (
    <div className={`px-1.5 py-0.5 text-xs rounded-full flex items-center ${isGood ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
      {isPositive ? (
        <ArrowUpRight className="h-3 w-3 mr-1" />
      ) : (
        <ArrowDownRight className="h-3 w-3 mr-1" />
      )}
      {prefix}{typeof value === 'number' ? (Math.abs(value) * 100).toFixed(1) + '%' : value}{suffix}
    </div>
  );
};

// Legacy Dashboard Card - now using enhanced version from ui/dashboard-card.tsx
// Keeping this for reference but using the enhanced DashboardCard component instead

// Loading skeleton component - now using enhanced LoadingCard
const MetricCardSkeleton = () => (
  <LoadingCard className="border rounded-lg" />
);

// Empty state component
const EmptyState = ({ message = "No data available", icon = Info }: EmptyStateProps) => (
  <div className="flex flex-col items-center justify-center p-6 text-center">
    <div className="rounded-full bg-gray-100 p-3 mb-4">
      {icon && React.createElement(icon, { className: "h-6 w-6 text-gray-400" })}
    </div>
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);

// Create a reusable refresh button component
const RefreshButton = ({ onClick, isLoading = false }: RefreshButtonProps) => (
  <Button 
    variant="brand" 
    size="sm" 
    className="h-9 hover-lift" 
    onClick={onClick}
    disabled={isLoading}
  >
    <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
    Refresh
  </Button>
);

// Reusable chart header component
const ChartHeader = ({ title, description, action }: ChartHeaderProps) => (
  <div className="flex items-center justify-between pb-2">
    <div>
      <CardTitle className="text-base">{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </div>
    {action}
  </div>
);

// Reusable chart container with error handling
const ChartContainer = ({ 
  children, 
  isLoading = false, 
  error = null, 
  height = "300px" 
}: { 
  children: React.ReactNode; 
  isLoading?: boolean; 
  error?: any; 
  height?: string;
}) => {
  if (isLoading) {
    return (
      <div className={`h-[${height}] flex items-center justify-center bg-gray-50`}>
        <div className="animate-pulse text-gray-400">Loading chart data...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`h-[${height}] flex items-center justify-center bg-gray-50 text-center p-4`}>
        <div>
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Failed to load chart data</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`h-[${height}]`}>
      {children}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [timeframe, setTimeframe] = useState('month');
  const [revenueMetric, setRevenueMetric] = useState('revenue');
  const [activeTab, setActiveTab] = useState('overview');
  const [trialInfo, setTrialInfo] = useState({
    daysLeft: 0,
    isTrialActive: false,
    trialEndDate: null as Date | null
  });
  const companyId = user?.companyId || '';
  
  // Enhanced query to get comprehensive dashboard metrics
  const { data: metrics, isLoading, error, refetch } = useQuery({
    queryKey: ['enhancedDashboardMetrics', timeframe, companyId],
    queryFn: () => getEnhancedDashboardMetrics(timeframe, companyId),
    refetchInterval: 300000, // Refetch every 5 minutes
  });
  
  // Fetch company settings
  const { data: companySettings } = useQuery({
    queryKey: ['companySettings', companyId],
    queryFn: () => getCompanySettings(companyId),
    enabled: !!companyId
  });

  const achGatewayEnabled = React.useMemo(() => isAchPaymentAvailable(companySettings), [companySettings]);
  
  // State for refresh functionality
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Payment Behavior Analysis state
  const [paymentBehaviorSort, setPaymentBehaviorSort] = useState<{field: string, direction: 'asc' | 'desc'}>({
    field: 'totalSpent',
    direction: 'desc'
  });
  const [paymentBehaviorFilter, setPaymentBehaviorFilter] = useState<'all' | 'good' | 'average' | 'poor'>('all');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  
  // Product restock state
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockProduct, setRestockProduct] = useState<any>(null);
  const [restockQuantity, setRestockQuantity] = useState<number>(0);

  const quickActions = React.useMemo(
    () => [
      {
        title: 'Create Invoice',
        description: 'Send a polished invoice in under a minute.',
        href: '/app/invoices/new',
        icon: FileText,
        accent: 'from-blue-500/40 via-indigo-500/20 to-transparent',
        iconBg: 'from-blue-500 via-blue-600 to-indigo-500',
        iconShadow: 'shadow-blue-500/30',
        textAccent: 'text-blue-600',
      },
      {
        title: 'Collect Payment',
        description: 'Charge cards or ACH right from the virtual terminal.',
        href: '/app/virtual-terminal',
        icon: CreditCard,
        accent: 'from-emerald-400/40 via-teal-400/20 to-transparent',
        iconBg: 'from-emerald-500 via-emerald-600 to-teal-500',
        iconShadow: 'shadow-emerald-500/30',
        textAccent: 'text-emerald-600',
      },
      {
        title: 'Invite Teammate',
        description: 'Give colleagues secure access with role controls.',
        href: '/app/settings?tab=team',
        icon: Users,
        accent: 'from-sky-400/40 via-cyan-400/20 to-transparent',
        iconBg: 'from-sky-500 via-sky-600 to-cyan-500',
        iconShadow: 'shadow-sky-500/30',
        textAccent: 'text-sky-600',
      },
      {
        title: 'View Reports',
        description: 'Review revenue, collections, and activity trends.',
        href: '/app/reports',
        icon: BarChart2,
        accent: 'from-violet-400/40 via-purple-400/20 to-transparent',
        iconBg: 'from-violet-500 via-violet-600 to-purple-500',
        iconShadow: 'shadow-purple-500/30',
        textAccent: 'text-violet-600',
      },
    ],
    []
  );
  
  // Handle error if present
  useEffect(() => {
    if (error) {
      // Error fetching enhanced dashboard metrics
    }
  }, [error]);
  
  // Timeframe handler - consolidated to a single function
  const handleTimeframeChange = (value: string) => {
    setTimeframe(value);
  };
  
  // Revenue metric handler for chart switching
  const handleRevenueMetricChange = (value: string) => {
    setRevenueMetric(value);
  };
  
  // Function to get the data key and formatter based on selected metric
  const getChartConfig = () => {
    switch (revenueMetric) {
      case 'invoices':
        return {
          dataKey: 'transactionCount',
          formatter: (value: number) => `${value} invoices`,
          yAxisFormatter: (value: number) => `${value}`,
          label: 'Invoice Count'
        };
      case 'average':
        return {
          dataKey: 'averageValue',
          formatter: (value: number) => formatCurrency(value),
          yAxisFormatter: (value: number) => `$${value.toLocaleString()}`,
          label: 'Average Value'
        };
      default:
        return {
          dataKey: 'revenue',
          formatter: (value: number) => formatCurrency(value),
          yAxisFormatter: (value: number) => `$${value.toLocaleString()}`,
          label: 'Revenue'
        };
    }
  };
  
  // Get trial information
  useEffect(() => {
    const fetchTrialInfo = async () => {
      if (user?.companyId) {
        try {
          const companyDoc = await getDoc(doc(db, 'companies', user.companyId));
          
          if (companyDoc.exists()) {
            const companyData = companyDoc.data();
            
            if (companyData.subscription?.plan === 'trial') {
              const endDate = companyData.subscription.endDate?.toDate();
              
              if (endDate) {
                const now = new Date();
                const diffTime = endDate.getTime() - now.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                
                setTrialInfo({
                  daysLeft: Math.max(0, diffDays),
                  isTrialActive: true,
                  trialEndDate: endDate
                });
              }
            }
          }
        } catch (error) {
          // Error fetching trial info
        }
      }
    };
    
    fetchTrialInfo();
  }, [user]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (err) {
      // Error refreshing data
    } finally {
      setIsRefreshing(false);
    }
  };

  // Consistent loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-end">
          <div className="flex items-center space-x-2">
            <Select defaultValue="month">
              <SelectTrigger className="w-32 h-9 text-sm">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Consistent error state
  if (error) {
    return (
      <div className="space-y-6">
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertTitle className="text-red-700">Error loading dashboard</AlertTitle>
          <AlertDescription className="text-red-600">
            There was a problem fetching your dashboard data. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Get the count of alerts
  const alertCount = metrics?.alerts?.length || 0;

  // Helper functions for Payment Behavior Analysis
  const sortPaymentBehaviorData = (data: any[]) => {
    if (!data) return [];
    
    return [...data].sort((a, b) => {
      const { field, direction } = paymentBehaviorSort;
      let aVal = a[field];
      let bVal = b[field];
      
      // Handle string comparisons
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const filterPaymentBehaviorData = (data: any[]) => {
    if (!data) return [];
    
    if (paymentBehaviorFilter === 'all') return data;
    
    return data.filter(customer => customer.status === paymentBehaviorFilter);
  };

  const handleSort = (field: string) => {
    setPaymentBehaviorSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getPaymentTrendIcon = (customer: any) => {
    // This would ideally come from historical data
    // For now, we'll simulate based on current performance
    if (customer.avgDaysToPay <= 5) return <TrendingDown className="h-3 w-3 text-green-500" />;
    if (customer.avgDaysToPay <= 15) return <ArrowUpDown className="h-3 w-3 text-gray-500" />;
    return <TrendingUp className="h-3 w-3 text-red-500" />;
  };

  return (
    <div className="space-y-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-700">
      {/* Header with timeframe selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
      <div className="flex items-center space-x-2">
        <Select value={timeframe} onValueChange={handleTimeframeChange}>
          <SelectTrigger className="w-32 h-9 text-sm">
            <SelectValue placeholder="Select timeframe" />
          </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          
          <RefreshButton onClick={handleRefresh} isLoading={isRefreshing} />
          
          {/* Notification Bell */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 relative">
                <Bell className="h-4 w-4" />
                {alertCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                    {alertCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-medium text-sm">Notifications</h3>
              </div>
              {alertCount > 0 ? (
                <div className="max-h-[300px] overflow-y-auto">
                  {metrics?.alerts?.map((alert, index) => (
                    <div 
                      key={index} 
                      className="p-3 border-b border-gray-100 last:border-0 hover:bg-gray-50"
                    >
                      <div className="flex items-start">
                        <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-900">{alert.message}</p>
                          {alert.description && (
                            <p className="text-xs text-gray-500 mt-1">{alert.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto mb-2" />
                  No notifications at this time
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Dashboard Tabs */}
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="financial">Financial</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Total Revenue */}
            <DashboardCard
              title="Total Revenue"
              value={formatCurrency(metrics?.totalRevenue || 0)}
              subtitle={`From ${metrics?.totalInvoices || 0} invoices`}
              icon={<DollarSign className="h-4 w-4 text-blue-500" />}
              trend={{
                value: metrics?.revenueGrowth ?? 0,
                label: ` vs previous ${timeframe}`,
                isPositiveGood: true
              }}
              variant="success"
            />

            {/* Outstanding Invoices */}
            <DashboardCard
              title="Outstanding"
              value={formatCurrency(metrics?.outstandingAmount || 0)}
              subtitle={`${metrics?.outstandingInvoices || 0} unpaid invoices`}
              icon={<Clock className="h-4 w-4 text-amber-500" />}
              variant="warning"
              footer={
                <>
                  <div className="flex justify-between mb-1 text-xs">
                    <span>Collection rate</span>
                    <span className="font-medium">{formatPercentage(metrics?.collectionRate || 0)}</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 rounded-full" 
                      style={{width: `${(metrics?.collectionRate || 0) * 100}%`}}
                    ></div>
                  </div>
                </>
              }
            />

            {/* Projected Cashflow */}
            <DashboardCard
              title="Upcoming Revenue"
              value={formatCurrency(metrics?.projectedRevenue || 0)}
              subtitle="Next 30 days"
              icon={<CalendarIcon className="h-4 w-4 text-green-500" />}
              variant="brand"
              footer={
                <div className="flex items-center space-x-1">
                  <div className="flex-1 h-1.5 bg-green-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full" 
                      style={{width: `${metrics?.recurringRevenuePercentage ?? 0}%`}}
                    ></div>
                  </div>
                  <span className="text-xs text-green-600">
                    {formatPercentage((metrics?.recurringRevenuePercentage ?? 0) / 100)} recurring
                  </span>
                </div>
              }
            />

            {/* Past Due */}
            <DashboardCard
              title="Past Due"
              value={formatCurrency(metrics?.overdueAmount || 0)}
              subtitle={`${metrics?.overdueInvoices || 0} overdue invoices`}
              icon={<AlertCircle className="h-4 w-4 text-red-500" />}
              variant="error"
              footer={
                <div className="grid grid-cols-4 gap-1">
                  <div className="bg-red-100 p-1 rounded">
                    <p className="text-xs text-center text-red-700">{formatCurrency(metrics?.aging30Days || 0)}</p>
                    <p className="text-[10px] text-center text-red-600">1-30</p>
                  </div>
                  <div className="bg-red-200 p-1 rounded">
                    <p className="text-xs text-center text-red-700">{formatCurrency(metrics?.aging60Days || 0)}</p>
                    <p className="text-[10px] text-center text-red-600">31-60</p>
                  </div>
                  <div className="bg-red-300 p-1 rounded">
                    <p className="text-xs text-center text-red-700">{formatCurrency(metrics?.aging90Days || 0)}</p>
                    <p className="text-[10px] text-center text-red-600">61-90</p>
                  </div>
                  <div className="bg-red-400 p-1 rounded">
                    <p className="text-xs text-center text-red-100">{formatCurrency(metrics?.aging90PlusDays || 0)}</p>
                    <p className="text-[10px] text-center text-red-100">91+</p>
                  </div>
                </div>
              }
            />
          </div>

          {/* Revenue and Expenses Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-base">Revenue Trends</CardTitle>
                  <CardDescription>Monthly revenue breakdown</CardDescription>
                </div>
                <Select value={revenueMetric} onValueChange={handleRevenueMetricChange}>
                  <SelectTrigger className="w-36 h-8 text-xs">
                    <SelectValue placeholder="View" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Revenue</SelectItem>
                    <SelectItem value="invoices">Invoice Count</SelectItem>
                    <SelectItem value="average">Average Value</SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={metrics?.revenueByMonth || []}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="month" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <YAxis 
                        tickFormatter={getChartConfig().yAxisFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => getChartConfig().formatter(value as number)}
                        labelFormatter={(label) => `Month: ${label}`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Area 
                        type="monotone"
                        dataKey={getChartConfig().dataKey}
                        stroke="#3b82f6"
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Status Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Invoice Status</CardTitle>
                <CardDescription>Current distribution</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center">
                <div className="w-full h-[230px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={metrics?.invoiceStatusDistribution || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {metrics?.invoiceStatusDistribution?.map((entry: InvoiceStatus, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={
                              entry.status === 'paid' ? '#22c55e' : 
                              entry.status === 'sent' ? '#3b82f6' :
                              entry.status === 'draft' ? '#6b7280' :
                              entry.status === 'overdue' ? '#ef4444' : '#f59e0b'
                            }
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`${value} invoices`, 'Count']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 w-full mt-2">
                  {metrics?.invoiceStatusDistribution?.map((status: InvoiceStatus, index) => (
                    <div key={index} className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ 
                          backgroundColor: 
                            status.status === 'paid' ? '#22c55e' : 
                            status.status === 'sent' ? '#3b82f6' :
                            status.status === 'draft' ? '#6b7280' :
                            status.status === 'overdue' ? '#ef4444' : '#f59e0b'
                        }}
                      ></div>
                      <span className="text-xs capitalize">{status.status} ({status.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Recent Activity</CardTitle>
                  <CardDescription>Latest financial transactions</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {metrics?.recentActivity?.map((activity: RecentActivity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${
                        activity.status === 'paid' 
                          ? 'bg-green-100 text-green-600' 
                          : activity.status === 'refunded'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-red-100 text-red-600'
                      }`}>
                        {activity.status === 'paid' ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : activity.status === 'refunded' ? (
                          <ArrowUpRight className="h-5 w-5" />
                        ) : (
                          <AlertCircle className="h-5 w-5" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {activity.customerCompanyName || activity.customerName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {activity.status === 'paid' ? 'Payment received' : 
                           activity.status === 'refunded' ? 'Payment refunded' : 'Payment failed'} 
                          <span className="mx-1">•</span>
                          {formatDate(activity.date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        activity.status === 'paid' ? 'text-green-600' : 
                        activity.status === 'refunded' ? 'text-gray-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(activity.amount)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {activity.paymentMethod === 'credit_card' ? 'Credit Card' : 'Bank Transfer'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4">
          {/* Financial Summary */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Financial Summary</CardTitle>
                  <CardDescription>Key financial metrics overview</CardDescription>
                </div>
                
                {/* Using the main timeframe selector, removed duplicate */}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Total Amount */}
                <div className="bg-white p-4 rounded-xl border shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Amount</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(metrics?.totalRevenue || 0)}</p>
                    </div>
                    <DollarSign className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="flex items-center mt-4">
                    <StatusIndicator 
                      value={metrics?.revenueGrowth ?? 0} 
                      suffix={` vs previous ${timeframe}`}
                    />
                  </div>
                </div>
                
                {/* Credit Card Payments */}
                <div className="bg-white p-4 rounded-xl border shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Credit Card Payments</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(metrics?.totalCreditCardPayments || 0)}
                      </p>
                    </div>
                    <CreditCard className="h-5 w-5 text-purple-500" />
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">% of Total Revenue</span>
                      <span className="text-xs font-medium">{formatPercentage(metrics?.paymentMethodDistribution?.creditCard || 0)}</span>
                    </div>
                  </div>
                </div>

                {/* Bank Transfers */}
                {achGatewayEnabled && (
                  <div className="bg-white p-4 rounded-xl border shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Bank Transfers</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">
                          {formatCurrency(metrics?.totalACHPayments || 0)}
                        </p>
                      </div>
                      <Building2 className="h-5 w-5 text-blue-500" />
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">% of Total Revenue</span>
                        <span className="text-xs font-medium">{formatPercentage(metrics?.paymentMethodDistribution?.ach || 0)}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Estimated Sales Tax */}
                <div className="bg-white p-4 rounded-xl border shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Sales Tax</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(metrics?.totalSalesTax || 0)}
                      </p>
                    </div>
                    <Receipt className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">% of Total Revenue</span>
                      <span className="text-xs font-medium">
                        {formatPercentage((metrics?.totalSalesTax || 0) / (metrics?.totalRevenue || 1))}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Platform Fees */}
                <div className="bg-white p-4 rounded-xl border shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Platform Fees</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(metrics?.totalPlatformFees || 0)}
                      </p>
                    </div>
                    <Percent className="h-5 w-5 text-indigo-500" />
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">% of Total Revenue</span>
                      <span className="text-xs font-medium">
                        {formatPercentage((metrics?.totalPlatformFees || 0) / (metrics?.totalRevenue || 1))}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Net Revenue */}
                <div className="bg-white p-4 rounded-xl border shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Net Revenue</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency((metrics?.totalRevenue || 0) - (metrics?.totalPlatformFees || 0) - (metrics?.totalSalesTax || 0))}
                      </p>
                    </div>
                    <TrendingUp className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">% of Total Revenue</span>
                      <span className="text-xs font-medium">
                        {formatPercentage(((metrics?.totalRevenue || 0) - (metrics?.totalPlatformFees || 0) - (metrics?.totalSalesTax || 0)) / (metrics?.totalRevenue || 1))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Revenue Breakdown and Profit Margins */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Revenue Breakdown</CardTitle>
                <CardDescription>Revenue by product category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics?.revenueByCategory || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="category"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <YAxis
                        tickFormatter={(value) => `$${value.toLocaleString()}`}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value as number)}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Bar dataKey="revenue" fill="#8884d8" radius={[4, 4, 0, 0]}>
                        {metrics?.revenueByCategory?.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 40}, 70%, 60%)`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Profit Margins */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Profit Margins</CardTitle>
                <CardDescription>By product category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {metrics?.profitMargins?.map((category, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm truncate">{category.name}</span>
                        <span className="text-sm font-medium">{formatPercentage(category.margin)}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{width: `${category.margin * 100}%`}}
                          ></div>
                        </div>
                        <span className="text-xs ml-2 w-16 text-right">
                          {formatCurrency(category.profit)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Overall Margin</span>
                    <span className="text-sm font-bold">{formatPercentage(metrics?.overallMargin ?? 0)}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full" 
                        style={{width: `${(metrics?.overallMargin ?? 0) * 100}%`}}
                      ></div>
                    </div>
                    <span className="text-xs ml-2 w-16 text-right font-medium">
                      {formatCurrency(metrics?.totalProfit ?? 0)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Financial Trend Analysis */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Financial Trend Analysis</CardTitle>
                  <CardDescription>Revenue vs. Cost analysis</CardDescription>
                </div>
                <Select defaultValue="6months">
                  <SelectTrigger className="w-36 h-8 text-xs">
                    <SelectValue placeholder="Time Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3months">Last 3 Months</SelectItem>
                    <SelectItem value="6months">Last 6 Months</SelectItem>
                    <SelectItem value="12months">Last 12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metrics?.revenueByMonth || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Revenue"
                    />
                    {/* Show actual cost data based on product costs */}
                    <Line
                      type="monotone"
                      dataKey="cost"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Cost"
                      strokeDasharray="5 5"
                    />
                    {/* Show profit line (Revenue - Cost) */}
                    <Line
                      type="monotone"
                      dataKey={(entry) => (entry.revenue || 0) - (entry.cost || 0)}
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Profit"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Aged Receivables */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Aged Receivables</CardTitle>
              <CardDescription>Outstanding invoices by age</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Current</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">1-30 Days</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">31-60 Days</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">61-90 Days</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">90+ Days</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics?.agedReceivables?.map((customer: CustomerAgingData, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">
                          {customer.name}
                        </td>
                        <td className="px-4 py-3 text-right text-green-600">{formatCurrency(customer.current)}</td>
                        <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(customer.days30)}</td>
                        <td className="px-4 py-3 text-right text-amber-700">{formatCurrency(customer.days60)}</td>
                        <td className="px-4 py-3 text-right text-red-600">{formatCurrency(customer.days90)}</td>
                        <td className="px-4 py-3 text-right text-red-700">{formatCurrency(customer.days90plus)}</td>
                        <td className="px-4 py-3 text-right font-medium">{formatCurrency(customer.total)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-medium">
                      <td className="px-4 py-3 text-gray-900">Total</td>
                      <td className="px-4 py-3 text-right text-green-600">{formatCurrency(metrics?.totalAgedReceivables?.current ?? 0)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(metrics?.totalAgedReceivables?.days30 ?? 0)}</td>
                      <td className="px-4 py-3 text-right text-amber-700">{formatCurrency(metrics?.totalAgedReceivables?.days60 ?? 0)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{formatCurrency(metrics?.totalAgedReceivables?.days90 ?? 0)}</td>
                      <td className="px-4 py-3 text-right text-red-700">{formatCurrency(metrics?.totalAgedReceivables?.days90plus ?? 0)}</td>
                      <td className="px-4 py-3 text-right font-bold">{formatCurrency(metrics?.totalAgedReceivables?.total ?? 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Cash Flow Projection */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Cash Flow Projection</CardTitle>
                  <CardDescription>Estimated incoming payments</CardDescription>
                </div>
                <div className="flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span> 
                  <span>Next 30 days: {formatCurrency(metrics?.cashFlowTotal ?? 0)}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metrics?.cashFlowForecast || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                      tickFormatter={(value) => `Day ${value}`}
                    />
                    <YAxis
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                      labelFormatter={(day) => {
                        const forecastItem = metrics?.cashFlowForecast?.[day - 1] as (CashFlowForecast | undefined);
                        return `Day ${day}: ${new Date(forecastItem?.date ?? '').toLocaleDateString()}`;
                      }}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-600">One-time Invoices</p>
                  <p className="text-lg font-semibold">{formatCurrency(metrics?.oneTimeInvoicesCount ? metrics.cashFlowTotal * (1 - metrics.recurringRevenuePercentage / 100) : 0)}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-green-600">Recurring Invoices</p>
                  <p className="text-lg font-semibold">{formatCurrency(metrics?.recurringRevenuePercentage ? metrics.cashFlowTotal * metrics.recurringRevenuePercentage / 100 : 0)}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg sm:col-span-1 col-span-2">
                  <p className="text-xs text-purple-600">Expected Collection Rate</p>
                  <p className="text-lg font-semibold">{formatPercentage(metrics?.collectionRate ?? 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Customers Tab */}
        <TabsContent value="customers" className="space-y-4">
          {/* Customer Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Customers</CardTitle>
                <div className="flex justify-between items-center">
                  <Users className="h-5 w-5 text-blue-500" />
                  <div className="px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full">Active</div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics?.totalCustomers || 0}</div>
                <div className="flex items-center mt-4">
                  <div className="px-1.5 py-0.5 text-xs bg-green-100 text-green-600 rounded-full flex items-center">
                    <ArrowUpRight className="h-3 w-3 mr-1" />
                    {metrics?.newCustomers || 0}
                  </div>
                  <span className="text-xs text-gray-500 ml-2">new this {timeframe}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Average Purchase</CardTitle>
                <div className="flex justify-between items-center">
                  <BarChart2 className="h-5 w-5 text-indigo-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(metrics?.averagePurchase || 0)}</div>
                <div className="flex items-center mt-4">
                  <div 
                    className={`px-1.5 py-0.5 text-xs rounded-full flex items-center ${(metrics?.averagePurchaseGrowth ?? 0) >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                  >
                    {(metrics?.averagePurchaseGrowth ?? 0) >= 0 ? (
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3 mr-1" />
                    )}
                    {formatPercentage(Math.abs(metrics?.averagePurchaseGrowth ?? 0))}
                  </div>
                  <span className="text-xs text-gray-500 ml-2">vs previous {timeframe}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Customer Retention</CardTitle>
                <div className="flex justify-between items-center">
                  <RefreshCcw className="h-5 w-5 text-green-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercentage(metrics?.retentionRate || 0)}</div>
                <div className="mt-4">
                  <div className="flex justify-between mb-1 text-xs">
                    <span>Repeat customers</span>
                    <span className="font-medium">{metrics?.repeatCustomersCount || 0}</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{width: `${Math.min((metrics?.retentionRate || 0) * 100, 100)}%`}}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Customer Lifetime Value</CardTitle>
                <div className="flex justify-between items-center">
                  <DollarSign className="h-5 w-5 text-emerald-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(metrics?.lifetimeValue || 0)}</div>
                <div className="flex items-center mt-4">
                  <div 
                    className={`px-1.5 py-0.5 text-xs rounded-full flex items-center ${(metrics?.lifetimeValueGrowth ?? 0) >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                  >
                    {(metrics?.lifetimeValueGrowth ?? 0) >= 0 ? (
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3 mr-1" />
                    )}
                    {formatPercentage(Math.abs(metrics?.lifetimeValueGrowth ?? 0))}
                  </div>
                  <span className="text-xs text-gray-500 ml-2">vs previous {timeframe}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customer Segmentation & Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Customer Type Distribution */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Customer Distribution</CardTitle>
                <CardDescription>By customer type</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={metrics?.customerTypeDistribution || []}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        labelLine={false}
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        dataKey="value"
                      >
                        {metrics?.customerTypeDistribution?.map((_: CustomerType, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={index === 0 ? '#3b82f6' : '#f97316'}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [`${value} customers`, name]}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-4">
                  {metrics?.customerTypeDistribution?.map((type: CustomerType, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded">
                      <p className="text-sm font-medium">{type.name}</p>
                      <div className="mt-1 flex justify-between items-center">
                        <span className="text-xs text-gray-500">{type.value} customers</span>
                        <span className="text-xs font-medium">
                          {formatCurrency(type.revenue)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Customer Activity */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Customer Activity</CardTitle>
                <CardDescription>Top 10 customers by activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      layout="vertical" 
                      data={metrics?.customerActivity?.slice(0, 10) || []}
                      margin={{ left: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                      <XAxis type="number" />
                      <YAxis 
                        type="category"
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        width={100}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value} transactions`, 'Activity']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Bar dataKey="transactions" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Customers List */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Top Customers</CardTitle>
                  <CardDescription>By total revenue contribution</CardDescription>
                </div>
                <Select defaultValue="revenue">
                  <SelectTrigger className="w-36 h-8 text-xs">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">By Revenue</SelectItem>
                    <SelectItem value="orders">By Orders</SelectItem>
                    <SelectItem value="recent">Most Recent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Type</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Invoices</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Total Spent</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Last Purchase</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">% of Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics?.topCustomers?.map((customer: Customer, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                              {customer.firstName?.charAt(0)}{customer.lastName?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                              </p>
                              <p className="text-xs text-gray-500">{customer.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{customer.type || 'N/A'}</td>
                        <td className="px-4 py-3 text-right">{customer.invoicesCount}</td>
                        <td className="px-4 py-3 text-right font-medium">{formatCurrency(customer.totalSpent)}</td>
                        <td className="px-4 py-3 text-right text-gray-500">30 days ago</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {formatPercentage(customer.percentageOfRevenue)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Enhanced Customer Payment Behavior Analysis */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Payment Behavior Analysis</CardTitle>
                  <CardDescription>Customer payment patterns and trends</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={paymentBehaviorFilter} onValueChange={(value: any) => setPaymentBehaviorFilter(value)}>
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <Filter className="h-3 w-3 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {metrics?.customerPaymentBehavior && metrics.customerPaymentBehavior.length > 0 ? (
                <div className="space-y-4">
                  {/* Payment Trend Chart */}
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Behavior Trend</h4>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={metrics?.paymentBehaviorTrend || []}>
                          <defs>
                            <linearGradient id="colorGood" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorAverage" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorPoor" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis 
                            dataKey="date" 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                            tickFormatter={(value) => format(new Date(value), 'MMM d')}
                          />
                          <YAxis 
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                            tickFormatter={(value) => `${value} days`}
                          />
                          <Tooltip 
                            formatter={(value) => [`${value} days`, 'Avg Payment Time']}
                            labelFormatter={(label) => format(new Date(label), 'MMM d, yyyy')}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="avgPaymentTime" 
                            stroke="#3b82f6" 
                            fillOpacity={1} 
                            fill="url(#colorGood)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Avg Payment Time</span>
                        <Timer className="h-4 w-4 text-gray-400" />
                      </div>
                      <p className="text-lg font-semibold mt-1">
                        {Math.round(
                          metrics.customerPaymentBehavior.reduce((sum, c) => sum + c.avgDaysToPay, 0) / 
                          metrics.customerPaymentBehavior.length
                        )} days
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Overall On-time Rate</span>
                        <CheckCircle2 className="h-4 w-4 text-gray-400" />
                      </div>
                      <p className="text-lg font-semibold mt-1">
                        {formatPercentage(
                          metrics.customerPaymentBehavior.reduce((sum, c) => sum + c.onTimeRate, 0) / 
                          metrics.customerPaymentBehavior.length / 100
                        )}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Payment Health</span>
                        <Activity className="h-4 w-4 text-gray-400" />
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <div className="flex gap-1">
                          {['good', 'average', 'poor'].map(status => {
                            const count = metrics.customerPaymentBehavior.filter(c => c.status === status).length;
                            return (
                              <div 
                                key={status}
                                className={`px-2 py-1 text-xs rounded ${
                                  status === 'good' ? 'bg-green-100 text-green-700' :
                                  status === 'average' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}
                              >
                                {count}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="px-4 py-2 text-left font-medium text-gray-500">
                            <button 
                              className="flex items-center gap-1 hover:text-gray-700"
                              onClick={() => handleSort('name')}
                            >
                              Customer
                              <ArrowUpDown className="h-3 w-3" />
                            </button>
                          </th>
                          <th className="px-4 py-2 text-left font-medium text-gray-500">
                            <button 
                              className="flex items-center gap-1 hover:text-gray-700"
                              onClick={() => handleSort('avgDaysToPay')}
                            >
                              Avg. Days to Pay
                              <ArrowUpDown className="h-3 w-3" />
                            </button>
                          </th>
                          <th className="px-4 py-2 text-right font-medium text-gray-500">
                            <button 
                              className="flex items-center gap-1 hover:text-gray-700 ml-auto"
                              onClick={() => handleSort('onTimeRate')}
                            >
                              On-time Rate
                              <ArrowUpDown className="h-3 w-3" />
                            </button>
                          </th>
                          <th className="px-4 py-2 text-right font-medium text-gray-500">
                            <button 
                              className="flex items-center gap-1 hover:text-gray-700 ml-auto"
                              onClick={() => handleSort('totalSpent')}
                            >
                              Total Spent
                              <ArrowUpDown className="h-3 w-3" />
                            </button>
                          </th>
                          <th className="px-4 py-2 text-center font-medium text-gray-500">Payment Method</th>
                          <th className="px-4 py-2 text-center font-medium text-gray-500">Status</th>
                          <th className="px-4 py-2 text-center font-medium text-gray-500">Trend</th>
                          <th className="px-4 py-2 text-center font-medium text-gray-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortPaymentBehaviorData(filterPaymentBehaviorData(metrics.customerPaymentBehavior)).map((customer, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">
                            {customer.companyName || customer.name}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              <span className={`mr-2 ${
                                customer.avgDaysToPay <= 5 ? 'text-green-600' :
                                customer.avgDaysToPay <= 15 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>
                                {customer.avgDaysToPay}
                              </span>
                              <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    customer.avgDaysToPay <= 5 ? 'bg-green-600' :
                                    customer.avgDaysToPay <= 15 ? 'bg-amber-600' :
                                    'bg-red-600'
                                  }`} 
                                  style={{width: `${Math.min(customer.avgDaysToPay / 30 * 100, 100)}%`}}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">{formatPercentage(customer.onTimeRate / 100)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(customer.totalSpent)}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
                              {customer.preferredPaymentMethod === 'credit_card' ? 'Credit Card' : 'Bank Transfer'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div 
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs ${
                                customer.status === 'good' ? 'bg-green-100 text-green-800' : 
                                customer.status === 'average' ? 'bg-amber-100 text-amber-800' : 
                                'bg-red-100 text-red-800'
                              }`}
                            >
                              {customer.status}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center">
                              {getPaymentTrendIcon(customer)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => {
                                setSelectedCustomerId(customer.id);
                                setShowPaymentDetails(true);
                              }}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[200px] text-center">
                  <Clock className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment History</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Payment behavior data will appear once customers start paying invoices.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => navigate('/invoices')}>
                    Create Invoice
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          {/* Product Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Total Products</CardTitle>
                <div className="flex justify-between items-center">
                  <ShoppingBag className="h-5 w-5 text-purple-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics?.totalProducts || 0}</div>
                <div className="flex justify-between mt-4 text-xs text-gray-500">
                  <span>{metrics?.activeProducts || 0} active</span>
                  <span>{metrics?.lowStockProducts?.length || 0} low stock</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Average Margin</CardTitle>
                <div className="flex justify-between items-center">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercentage(metrics?.averageMargin || 0)}</div>
                <div className="mt-4">
                  <div className="flex justify-between mb-1 text-xs">
                    <span>Overall profitability</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full" 
                      style={{width: `${(metrics?.averageMargin || 0) * 100}%`}}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Top Selling</CardTitle>
                <div className="flex justify-between items-center">
                  <BarChart2 className="h-5 w-5 text-amber-500" />
                </div>
              </CardHeader>
              <CardContent>
                {metrics?.topSellingProduct ? (
                  <>
                    <div className="text-lg font-bold truncate" style={{ maxWidth: '180px' }}>
                      {metrics.topSellingProduct.name}
                    </div>
                    <div className="text-sm mt-1">{formatCurrency(metrics.topSellingProduct.revenue)}</div>
                    <div className="flex items-center mt-4">
                      <div className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full">
                        {metrics.topSellingProduct.unitsSold} units
                      </div>
                      <span className="text-xs text-gray-500 ml-2">this {timeframe}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-gray-400">
                      No sales yet
                    </div>
                    <div className="text-sm mt-1 text-gray-400">{formatCurrency(0)}</div>
                    <div className="text-xs text-gray-500 mt-4">Products will appear here once sold</div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Most Profitable</CardTitle>
                <div className="flex justify-between items-center">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
              </CardHeader>
              <CardContent>
                {metrics?.mostProfitableProduct ? (
                  <>
                    <div className="text-lg font-bold truncate" style={{ maxWidth: '180px' }}>
                      {metrics.mostProfitableProduct.name}
                    </div>
                    <div className="text-sm mt-1 text-green-600">
                      {formatPercentage(metrics.mostProfitableProduct.margin)} margin
                    </div>
                    <div className="flex items-center mt-4">
                      <div className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                        {formatCurrency(metrics.mostProfitableProduct.profit)}
                      </div>
                      <span className="text-xs text-gray-500 ml-2">profit this {timeframe}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-bold text-gray-400">
                      No profit data
                    </div>
                    <div className="text-sm mt-1 text-gray-400">
                      0% margin
                    </div>
                    <div className="text-xs text-gray-500 mt-4">Add product costs to see profitability</div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Product Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Products by Revenue</CardTitle>
                <CardDescription>Performance this {timeframe}</CardDescription>
              </CardHeader>
              <CardContent>
                {metrics?.topProductsByRevenue && metrics.topProductsByRevenue.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={metrics.topProductsByRevenue.slice(0, 5)}
                        layout="vertical"
                        margin={{ left: 150, right: 20, top: 10, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          axisLine={false}
                          tickLine={false}
                          width={140}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value as number)}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Bar dataKey="revenue" fill="#8884d8" radius={[0, 4, 4, 0]}>
                          {metrics.topProductsByRevenue.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={`hsl(${index * 30}, 70%, 50%)`} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex flex-col items-center justify-center text-center">
                    <ShoppingBag className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Product Sales Yet</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Once you create products and start selling, you'll see sales data here.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => navigate('/products')}>
                      Add Products
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inventory Health */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Inventory Health</CardTitle>
                <CardDescription>Stock level status</CardDescription>
              </CardHeader>
              <CardContent>
                {metrics?.inventoryHealthDistribution && 
                 metrics.inventoryHealthDistribution.some(item => item.value > 0) ? (
                  <>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={metrics.inventoryHealthDistribution.filter(item => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {metrics.inventoryHealthDistribution
                              .filter(item => item.value > 0)
                              .map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={
                                    entry.status === 'good' ? '#22c55e' : 
                                    entry.status === 'warning' ? '#f59e0b' :
                                    '#ef4444'
                                  }
                                />
                              ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => [`${value} products`, 'Count']}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      {metrics.inventoryHealthDistribution.map((status, index) => (
                        <div key={index} className="text-center">
                          <div 
                            className="w-4 h-4 rounded-full mx-auto mb-1"
                            style={{ 
                              backgroundColor: 
                                status.status === 'good' ? '#22c55e' : 
                                status.status === 'warning' ? '#f59e0b' :
                                '#ef4444'
                            }}
                          ></div>
                          <p className="text-xs font-medium capitalize">{status.status}</p>
                          <p className="text-xs text-gray-500">{status.value} products</p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[260px] text-center">
                    <ShoppingBag className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-1">No Inventory Data</h3>
                    <p className="text-xs text-gray-500">
                      Add physical products with stock levels to see inventory health.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Product Profitability */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Product Profitability Analysis</CardTitle>
              <CardDescription>Margin and revenue comparison</CardDescription>
            </CardHeader>
            <CardContent>
              {metrics?.productProfitability && metrics.productProfitability.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="px-4 py-2 text-left font-medium text-gray-500">Product</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-500">Category</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Units Sold</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Revenue</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Cost</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Profit</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.productProfitability.slice(0, 10).map((product, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">
                            <div className="max-w-[150px] truncate" title={product.name}>
                              {product.name}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{product.category || 'Uncategorized'}</td>
                          <td className="px-4 py-3 text-right">{product.unitsSold || 0}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(product.revenue || 0)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(product.cost || 0)}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(product.profit || 0)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs ${
                              product.margin >= 0.4 ? 'bg-green-100 text-green-800' : 
                              product.margin >= 0.2 ? 'bg-amber-100 text-amber-800' : 
                              product.margin > 0 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {formatPercentage(product.margin || 0)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[300px] text-center">
                  <BarChart2 className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Add products to your inventory to see profitability analysis.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => navigate('/products')}>
                    Add Products
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Low Stock Products */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Low Stock Alert</CardTitle>
                  <CardDescription>Products requiring attention</CardDescription>
                </div>
                <div className="px-2.5 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {metrics?.lowStockProducts?.length || 0} products
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {metrics?.lowStockProducts?.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metrics.lowStockProducts.slice(0, 6).map((product, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between">
                        <h3 className="font-medium text-gray-900 truncate" style={{ maxWidth: '180px' }}>{product.name}</h3>
                        <div className={`px-1.5 py-0.5 text-xs rounded-full ${
                          (product.stockQuantity / product.lowStockAlert) <= 0.5 ? 
                          'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          Critical
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{product.categoryName || product.category}</p>
                      <div className="mt-3">
                        <div className="flex justify-between mb-1 text-xs">
                          <span>Current stock</span>
                          <span className="font-medium">{product.stockQuantity}/{product.lowStockAlert}</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              (product.stockQuantity / product.lowStockAlert) <= 0.5 ? 
                              'bg-red-500' : 'bg-amber-500'
                            }`}
                            style={{ width: `${(product.stockQuantity / product.lowStockAlert) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          SKU: {product.sku || 'N/A'}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 text-xs"
                          onClick={() => {
                            setRestockProduct(product);
                            setRestockQuantity(product.lowStockAlert - product.stockQuantity || 10);
                            setShowRestockModal(true);
                          }}
                        >
                          Restock
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[200px] text-center">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mb-2" />
                  <p className="text-sm text-gray-600">All products have sufficient stock levels</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-4">
          {/* Operations Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DashboardCard
              title="Average Payment Time"
              value={`${metrics?.averagePaymentTime || 0} days`}
              icon={<Clock className="h-5 w-5 text-blue-500" />}
              trend={{
                value: metrics?.paymentTimeChange ?? 0,
                label: ` vs previous ${timeframe}`,
                isPositiveGood: false
              }}
            />

            <DashboardCard
              title="Payment Success Rate"
              value={formatPercentage(metrics?.paymentSuccessRate || 0)}
              icon={<CheckCircle2 className="h-5 w-5 text-green-500" />}
              footer={
                <>
                  <div className="flex justify-between mb-1 text-xs">
                    <span>Success rate</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full" 
                      style={{width: `${(metrics?.paymentSuccessRate || 0) * 100}%`}}
                    ></div>
                  </div>
                </>
              }
            />
            
            {/* Payment Method Split */}
            <DashboardCard
              title="Payment Method Split"
              value={formatPercentage(metrics?.paymentMethodDistribution?.creditCard || 0)}
              subtitle="Credit Card usage"
              icon={<CreditCard className="h-5 w-5 text-purple-500" />}
              footer={
                <>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="bg-purple-50 p-2 rounded text-center">
                      <p className="text-xs text-purple-700">Credit Card</p>
                      <p className="text-lg font-bold text-purple-800">
                        {formatPercentage(metrics?.paymentMethodDistribution?.creditCard || 0)}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded text-center">
                      <p className="text-xs text-blue-700">Bank Transfer</p>
                      <p className="text-lg font-bold text-blue-800">
                        {formatPercentage(metrics?.paymentMethodDistribution?.ach || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 text-center mt-2">
                    Based on {metrics?.totalTransactions || 0} transactions
                  </div>
                </>
              }
            />
            
            <DashboardCard
              title="Processing Fee Ratio"
              value={formatPercentage(metrics?.processingFeeRatio || 0)}
              icon={<DollarSign className="h-5 w-5 text-amber-500" />}
              trend={{
                value: metrics?.processingFeeRatioChange ?? 0,
                label: ` vs previous ${timeframe}`,
                isPositiveGood: false
              }}
            />
          </div>

          {/* Invoice Processing Efficiency */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Invoice Processing Time</CardTitle>
                <CardDescription>Average days from creation to payment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={metrics?.invoiceProcessingTime || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => [`${value} days`, 'Processing Time']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="days"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Payment Processing Success/Failure */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Payment Processing</CardTitle>
                <CardDescription>Success and failure rates over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics?.paymentProcessing || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#6b7280', fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => [`${value} transactions`, '']}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Legend />
                      <Bar dataKey="success" stackId="a" fill="#22c55e" name="Success" />
                      <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Failure Analysis */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Payment Failure Analysis</CardTitle>
              <CardDescription>Understanding payment failures</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Failure Reasons Pie Chart */}
                <div>
                  <h3 className="text-sm font-medium mb-4">Failure Reasons</h3>
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={metrics?.paymentFailureReasons || []}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          labelLine={false}
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {metrics?.paymentFailureReasons?.map((_, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={`hsl(${index * 40}, 70%, 45%)`} 
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [`${value} failures`, name]}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {metrics?.paymentFailureReasons?.map((reason, index) => (
                      <div key={index} className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: `hsl(${index * 40}, 70%, 45%)` }}
                        ></div>
                        <span className="text-xs truncate">{reason.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actionable Insights */}
                <div>
                  <h3 className="text-sm font-medium mb-4">Actionable Insights</h3>
                  <div className="space-y-3">
                    {metrics?.paymentFailureInsights?.map((insight, index) => (
                      <div key={index} className={`p-3 rounded-lg ${
                        insight.type === 'critical' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
                      }`}>
                        <p className="text-sm font-medium">{insight.title}</p>
                        <p className="text-xs mt-1">{insight.description}</p>
                      </div>
                    ))}
                    
                    {metrics?.paymentRecommendations?.map((recommendation, index) => (
                      <div key={index} className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-800">{recommendation.title}</p>
                        <p className="text-xs mt-1 text-blue-700">{recommendation.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Real-time Payment Monitoring */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Recent Payments</CardTitle>
                  <CardDescription>Latest transaction processing history</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="h-8 text-xs flex items-center">
                  <RefreshCcw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Date</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Customer</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Payment Method</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Transaction ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics?.recentActivity?.slice(0, 5).map((transaction: Transaction, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(transaction.date)}
                        </td>
                        <td className="px-4 py-3 text-gray-900">
                          {transaction.customerCompanyName || transaction.customerName}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {formatCurrency(transaction.amount)}
                        </td>
                        <td className="px-4 py-3">
                          {transaction.paymentMethod === 'credit_card' ? 
                            'Credit Card' + (transaction.cardLast4 ? ` (${transaction.cardLast4})` : '') : 
                            'Bank Transfer'}
                        </td>
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            transaction.status === 'paid' ? 'bg-green-100 text-green-800' : 
                            transaction.status === 'refunded' ? 'bg-gray-100 text-gray-800' : 
                            'bg-red-100 text-red-800'
                          }`}>
                            {transaction.status === 'paid' ? 'Success' : 
                             transaction.status === 'refunded' ? 'Refunded' : 'Failed'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate">
                          {transaction.transactionId || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Payment Details Modal */}
      <Dialog open={showPaymentDetails} onOpenChange={setShowPaymentDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment History Details</DialogTitle>
            <DialogDescription>
              View detailed payment history and trends for this customer
            </DialogDescription>
          </DialogHeader>
          
          {selectedCustomerId && metrics?.customerPaymentBehavior && (
            <div className="space-y-4">
              {(() => {
                const customer = metrics.customerPaymentBehavior.find(c => c.id === selectedCustomerId);
                if (!customer) return <p>Customer data not found.</p>;
                
                return (
                  <>
                    {/* Customer Summary */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">{customer.companyName || customer.name}</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Average Payment Time:</span>
                          <p className="font-medium">{customer.avgDaysToPay} days</p>
                        </div>
                        <div>
                          <span className="text-gray-500">On-time Rate:</span>
                          <p className="font-medium">{formatPercentage(customer.onTimeRate / 100)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Total Revenue:</span>
                          <p className="font-medium">{formatCurrency(customer.totalSpent)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Payment Status:</span>
                          <p className={`font-medium ${
                            customer.status === 'good' ? 'text-green-600' :
                            customer.status === 'average' ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Payment Trend Chart */}
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="font-medium mb-3">Payment Time Trend</h4>
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={metrics?.customerPaymentTrend?.[selectedCustomerId] || []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(value) => format(new Date(value), 'MMM d')}
                            />
                            <YAxis />
                            <Tooltip 
                              labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                              formatter={(value) => [`${value} days`, 'Payment Time']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="daysToPayment" 
                              stroke="#3b82f6" 
                              strokeWidth={2}
                              dot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    {/* Recent Payments */}
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="font-medium mb-3">Recent Payments</h4>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">
                          Feature coming soon: Detailed payment history will show individual transactions, 
                          payment methods, and late fee information.
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/customers/${selectedCustomerId}`)}
                      >
                        View Customer Profile
                      </Button>
                      <Button
                        variant="brand"
                        onClick={() => navigate(`/invoices?customerId=${selectedCustomerId}`)}
                      >
                        View Invoices
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Restock Product Modal */}
      <Dialog open={showRestockModal} onOpenChange={setShowRestockModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Restock Product</DialogTitle>
            <DialogDescription>
              Add inventory for {restockProduct?.name}
            </DialogDescription>
          </DialogHeader>
          
          {restockProduct && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Current Stock:</span>
                  <span className="font-medium">{restockProduct.stockQuantity || 0} units</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Low Stock Alert:</span>
                  <span className="font-medium">{restockProduct.lowStockAlert || 5} units</span>
                </div>
                {restockProduct.sku && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">SKU:</span>
                    <span className="font-medium">{restockProduct.sku}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="restockQuantity">Quantity to Add</Label>
                <Input
                  id="restockQuantity"
                  type="number"
                  min="1"
                  value={restockQuantity}
                  onChange={(e) => setRestockQuantity(parseInt(e.target.value) || 0)}
                  placeholder="Enter quantity to add"
                />
                <p className="text-sm text-gray-500">
                  New total will be: {(restockProduct.stockQuantity || 0) + restockQuantity} units
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRestockModal(false);
                setRestockProduct(null);
                setRestockQuantity(0);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              onClick={async () => {
                if (restockProduct && restockQuantity > 0) {
                  try {
                    const newQuantity = (restockProduct.stockQuantity || 0) + restockQuantity;
                    await updateProduct(restockProduct.id, {
                      stockQuantity: newQuantity
                    });
                    
                    // Refresh the dashboard data
                    await refetch();
                    
                    // Show success toast
                    toast({
                      title: "Stock Updated",
                      description: `Successfully added ${restockQuantity} units to ${restockProduct.name}`,
                    });
                    
                    setShowRestockModal(false);
                    setRestockProduct(null);
                    setRestockQuantity(0);
                  } catch (error) {
                    // Error restocking product
                    toast({
                      title: "Error",
                      description: "Failed to update stock. Please try again.",
                      variant: "destructive",
                    });
                  }
                }
              }}
              disabled={!restockQuantity || restockQuantity <= 0}
            >
              Add Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
